import type { DayHours, Dispensary, HoursSettingsForOrderType } from '@dutchiesdk/ecommerce-extensions-sdk';

import type {
  Dispensaries,
  HoursSettingsForOrderType as HoursSettingsForOrderTypeGql,
  DayBounds,
  GetChainDispensariesQuery,
  HoursSettingsForOrderTypeFragmentFragment,
} from 'src/gql/graphql';
import { USStatesMap } from 'shared/constants/cities-geography';

type ChainDispensary = NonNullable<GetChainDispensariesQuery['filteredDispensaries']>[number];

// this function is called with both full dispensaries and also chain dispensaries
// they're two different gql endpoints each with slightly different data coming back
// mostly, chain has ways less values
function isFullDispensary(dispensary?: ChainDispensary | Dispensaries | null): dispensary is Dispensaries {
  if (!dispensary) {
    return false;
  }

  if ('email' in dispensary || 'menuUrl' in dispensary) {
    return true;
  }

  return false;
}

export const mapDispensaryToRemote = (dispensary?: ChainDispensary | Dispensaries | null): Dispensary | undefined => {
  if (!dispensary) {
    return undefined;
  }

  const enabledOrderTypes = dispensary.enabledOrderTypes ?? {};

  return {
    address: {
      city: dispensary.location?.city ?? '',
      // The SDK types `address.state` as a required string. A dispensary
      // with no location/state on file is a pre-existing data-integrity gap
      // (not enforced at the DB schema level) rather than a real case to
      // design around - per team discussion, a dispensary in that state is
      // already non-functional for ecommerce regardless of this field, so
      // fall back to '' like the other address fields instead of undefined.
      state: (dispensary.location?.state ? USStatesMap[dispensary.location.state] : undefined) ?? '',
      stateAbbreviation: dispensary.location?.state ?? '',
      street1: dispensary.location?.ln1 ?? '',
      street2: dispensary.location?.ln2 ?? '',
      zip: isFullDispensary(dispensary) ? dispensary.location?.zipcode ?? '' : '',
    },
    chain: dispensary.chain ?? '',
    cname: dispensary.cName ?? '',
    email: isFullDispensary(dispensary) ? dispensary.email ?? '' : '',
    hours: {
      curbsidePickup: mapDispensaryHoursSettingsToRemote(dispensary.hoursSettings?.curbsidePickup),
      delivery: mapDispensaryHoursSettingsToRemote(dispensary.hoursSettings?.delivery),
      driveThruPickup: mapDispensaryHoursSettingsToRemote(dispensary.hoursSettings?.driveThruPickup),
      inStorePickup: mapDispensaryHoursSettingsToRemote(dispensary.hoursSettings?.inStorePickup),
    },
    id: dispensary.id,
    images: {
      logo: dispensary.logoImage ?? '',
    },
    links: {
      storeFrontRoot: `/stores/${String(dispensary.cName ?? '')}`,
      website: isFullDispensary(dispensary) ? dispensary.menuUrl ?? '' : '',
    },
    medDispensary: isFullDispensary(dispensary) ? dispensary.medicalDispensary ?? false : false,
    name: dispensary.name ?? '',
    orderTypes: {
      curbsidePickup: !!enabledOrderTypes.curbsidePickup,
      delivery: !!enabledOrderTypes.delivery,
      driveThruPickup: !!enabledOrderTypes.driveThruPickup,
      inStorePickup: !!enabledOrderTypes.inStorePickup,
      kiosk: !!enabledOrderTypes.kiosk,
      pickup: !!enabledOrderTypes.pickup,
    },
    orderTypesConfig: {
      curbsidePickup: {
        enableAfterHoursOrdering: dispensary.orderTypesConfigV2?.curbsidePickup?.enableAfterHoursOrdering ?? false,
        enableASAPOrdering: dispensary.orderTypesConfigV2?.curbsidePickup?.enableASAPOrdering ?? false,
        enableScheduledOrdering: dispensary.orderTypesConfigV2?.curbsidePickup?.enableScheduledOrdering ?? false,
      },
      delivery: {
        enableAfterHoursOrdering: dispensary.orderTypesConfigV2?.delivery?.enableAfterHoursOrdering ?? false,
        enableASAPOrdering: dispensary.orderTypesConfigV2?.delivery?.enableASAPOrdering ?? false,
        enableScheduledOrdering: dispensary.orderTypesConfigV2?.delivery?.enableScheduledOrdering ?? false,
      },
      driveThruPickup: {
        enableAfterHoursOrdering: dispensary.orderTypesConfigV2?.driveThruPickup?.enableAfterHoursOrdering ?? false,
        enableASAPOrdering: dispensary.orderTypesConfigV2?.driveThruPickup?.enableASAPOrdering ?? false,
        enableScheduledOrdering: dispensary.orderTypesConfigV2?.driveThruPickup?.enableScheduledOrdering ?? false,
      },
      inStorePickup: {
        enableAfterHoursOrdering: dispensary.orderTypesConfigV2?.inStorePickup?.enableAfterHoursOrdering ?? false,
        enableASAPOrdering: dispensary.orderTypesConfigV2?.inStorePickup?.enableASAPOrdering ?? false,
        enableScheduledOrdering: dispensary.orderTypesConfigV2?.inStorePickup?.enableScheduledOrdering ?? false,
      },
      offerAnyPickupService: dispensary.orderTypesConfigV2?.offerAnyPickupService ?? false,
      offerDeliveryService: dispensary.orderTypesConfigV2?.offerDeliveryService ?? false,
    },
    phone: dispensary.phone ?? '',
    recDispensary: isFullDispensary(dispensary) ? dispensary.recDispensary ?? false : false,
    status: dispensary.status ?? '',
  };
};

const mapDispensaryHoursSettingsToRemote = (
  hoursSettings: HoursSettingsForOrderTypeFragmentFragment | HoursSettingsForOrderTypeGql | null | undefined
): HoursSettingsForOrderType => {
  // Chain dispensaries use 'hours', full dispensaries use 'effectiveHours'
  const effectiveHours =
    hoursSettings && 'effectiveHours' in hoursSettings
      ? hoursSettings.effectiveHours ?? hoursSettings.hours
      : hoursSettings?.hours;

  return {
    effectiveHours: {
      Monday: mapEffectiveHoursToRemote(effectiveHours?.Monday),
      Tuesday: mapEffectiveHoursToRemote(effectiveHours?.Tuesday),
      Wednesday: mapEffectiveHoursToRemote(effectiveHours?.Wednesday),
      Thursday: mapEffectiveHoursToRemote(effectiveHours?.Thursday),
      Friday: mapEffectiveHoursToRemote(effectiveHours?.Friday),
      Saturday: mapEffectiveHoursToRemote(effectiveHours?.Saturday),
      Sunday: mapEffectiveHoursToRemote(effectiveHours?.Sunday),
    },
    enabled: !!hoursSettings,
  };
};

const mapEffectiveHoursToRemote = (effectiveHours: DayBounds | null | undefined): DayHours => ({
  active: effectiveHours?.active ?? false,
  end: effectiveHours?.end ?? undefined,
  start: effectiveHours?.start ?? undefined,
});
